import React from 'react';
import './ErrorBoundary.css';

/**
 * Global Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs error details, and displays a fallback UI.
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('🔴 ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);

    // Update state with error details
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Optional: Send error to backend logging service
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error, errorInfo) => {
    // Only log in production to avoid noise during development
    if (process.env.NODE_ENV === 'production') {
      try {
        // TODO: Uncomment when backend endpoint is ready
        // fetch('/api/errors', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({
        //     message: error.toString(),
        //     stack: error.stack,
        //     componentStack: errorInfo.componentStack,
        //     userAgent: navigator.userAgent,
        //     timestamp: new Date().toISOString(),
        //     url: window.location.href,
        //   }),
        // });

        // For now, just log to console
        console.log('Error logged (production mode):', {
          message: error.toString(),
          timestamp: new Date().toISOString(),
        });
      } catch (loggingError) {
        console.error('Failed to log error to service:', loggingError);
      }
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div className="error-boundary-container">
          <div className="error-boundary-card">
            <div className="error-boundary-icon">⚠️</div>

            <h1 className="error-boundary-title">
              Etwas ist schiefgelaufen
            </h1>

            <p className="error-boundary-message">
              Die Anwendung ist auf einen unerwarteten Fehler gestoßen.
              Bitte laden Sie die Seite neu oder kontaktieren Sie den Support,
              wenn das Problem weiterhin besteht.
            </p>

            {/* Development Mode: Show error details */}
            {isDevelopment && this.state.error && (
              <div className="error-boundary-details">
                <h3>Fehlerdetails (nur im Entwicklungsmodus sichtbar):</h3>
                <div className="error-boundary-error-message">
                  <strong>Fehlermeldung:</strong>
                  <pre>{this.state.error.toString()}</pre>
                </div>
                {this.state.error.stack && (
                  <div className="error-boundary-stack">
                    <strong>Stack Trace:</strong>
                    <pre>{this.state.error.stack}</pre>
                  </div>
                )}
                {this.state.errorInfo?.componentStack && (
                  <div className="error-boundary-component-stack">
                    <strong>Component Stack:</strong>
                    <pre>{this.state.errorInfo.componentStack}</pre>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="error-boundary-actions">
              <button
                onClick={this.handleReload}
                className="error-boundary-btn error-boundary-btn-primary"
              >
                Seite neu laden
              </button>

              <button
                onClick={this.handleReset}
                className="error-boundary-btn error-boundary-btn-secondary"
              >
                Erneut versuchen
              </button>
            </div>

            {/* Error Count (for debugging repeated errors) */}
            {this.state.errorCount > 1 && (
              <p className="error-boundary-count">
                Fehler ist {this.state.errorCount} Mal aufgetreten
              </p>
            )}

            {/* Support Info */}
            <div className="error-boundary-footer">
              <p>
                Bei anhaltenden Problemen wenden Sie sich bitte an den Support
                mit folgenden Informationen:
              </p>
              <ul>
                <li>Zeitpunkt: {new Date().toLocaleString('de-DE')}</li>
                <li>Seite: {window.location.pathname}</li>
                {isDevelopment && this.state.error && (
                  <li>Fehler: {this.state.error.message}</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
